// Drive file lifecycle API — approve/lock + public-token mint (Phase 3 FE).
//
// USE_REAL_API-gated (real /drive/files/* in prod, MSW sim in dev). The
// public-token mint returns the plaintext token ONCE — the caller renders it as
// a QR / copyable link and never refetches it.

import { apiUrl } from '../lib/api'
import type { AttachmentRow } from './attachments'

export async function approveFile(fileId: string): Promise<AttachmentRow> {
  const res = await fetch(apiUrl(`/drive/files/${fileId}/approve`), { method: 'POST' })
  if (!res.ok) throw new Error(`approve failed: ${res.status}`)
  return res.json()
}

export async function patchFilePurpose(
  fileId: string,
  filePurpose: string,
): Promise<AttachmentRow> {
  const res = await fetch(apiUrl(`/drive/files/${fileId}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ file_purpose: filePurpose }),
  })
  if (!res.ok) throw new Error(`file_purpose update failed: ${res.status}`)
  return res.json()
}

export async function setLock(fileId: string, locked: boolean): Promise<AttachmentRow> {
  const res = await fetch(apiUrl(`/drive/files/${fileId}/lock`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ locked }),
  })
  if (!res.ok) throw new Error(`lock failed: ${res.status}`)
  return res.json()
}

export type PublicTokenMint = {
  token_id: string
  token: string
  resolve_path: string
  expires_at: string
  max_uses: number | null
}

export async function mintPublicToken(
  fileId: string,
  opts?: { purpose?: string; maxUses?: number | null; expiresInSeconds?: number },
): Promise<PublicTokenMint> {
  const body: Record<string, unknown> = {}
  if (opts?.purpose) body.purpose = opts.purpose
  if (opts?.maxUses != null) body.max_uses = opts.maxUses
  if (opts?.expiresInSeconds != null) body.expires_in_seconds = opts.expiresInSeconds
  const res = await fetch(apiUrl(`/drive/files/${fileId}/public-token`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => null)
    throw new Error(err?.detail?.message ?? `mint failed: ${res.status}`)
  }
  return res.json()
}

/** Absolute resolve URL for a minted token — what the QR encodes. */
export function publicResolveUrl(resolvePath: string): string {
  if (typeof window === 'undefined') return resolvePath
  return `${window.location.origin}${resolvePath}`
}
